import { createApi } from '@reduxjs/toolkit/query/react';
import { FloorPlan, FloorPlanListResponse, FloorPlanUploadInput, FloorPlanUpdateInput } from '../../types/floorPlan.types';
import { createExtendedTokenBaseQuery } from './baseQuery';

export interface GetFloorPlansParams {
  search?: string;
  location?: string;
  location_name?: string;
  file_type?: 'PDF' | 'JPG' | 'PNG';
  processing_status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  page?: number;
  limit?: number;
  ordering?: string;
  created_at_after?: string;
  created_at_before?: string;
  is_deleted?: boolean;
}

export const floorPlanApi = createApi({
  reducerPath: 'floorPlanApi',
  baseQuery: createExtendedTokenBaseQuery('floor-plans'),
  tagTypes: ['FloorPlan'],
  endpoints: (builder) => ({
    getFloorPlans: builder.query<FloorPlanListResponse, GetFloorPlansParams>({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });
        
        return {
          url: 'list/',
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['FloorPlan'],
    }),
    getFloorPlanById: builder.query<FloorPlan, string>({
      query: (id) => `list/${id}/`,
      providesTags: (result, error, id) => [{ type: 'FloorPlan', id }],
    }),
    uploadFloorPlan: builder.mutation<FloorPlan, FloorPlanUploadInput>({
      query: (floorPlan) => {
        const formData = new FormData();
        formData.append('name', floorPlan.name);
        
        if (floorPlan.description) {
          formData.append('description', floorPlan.description);
        }
        
        if (floorPlan.location) {
          formData.append('location_id', floorPlan.location);
        }
        
        formData.append('file', floorPlan.file);
        
        return {
          url: 'upload/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['FloorPlan'],
    }),
    updateFloorPlan: builder.mutation<FloorPlan, { id: string; data: FloorPlanUpdateInput }>({
      query: ({ id, data }) => ({
        url: `list/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'FloorPlan', id }],
    }),
    deleteFloorPlan: builder.mutation<void, string>({
      query: (id) => ({
        url: `list/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['FloorPlan'],
    }),
  }),
});

export const {
  useGetFloorPlansQuery,
  useGetFloorPlanByIdQuery,
  useUploadFloorPlanMutation,
  useUpdateFloorPlanMutation,
  useDeleteFloorPlanMutation,
} = floorPlanApi;